<template>
  <div class="h-screen overflow-hidden flex items-center justify-center p-4 auth-background">
    <div class="relative max-w-md w-full">
      <div class="glassmorphic-card">
        <div class="text-center mb-6">
          <router-link to="/" class="inline-block mb-4">
            <img 
              src="/images/venmas-logo-1.png" 
              alt="Venmas" 
              class="h-16 mx-auto object-contain"
            />
          </router-link>
          <h2 class="text-3xl font-bold text-white mb-2">Welcome Back</h2>
          <p class="text-white/70">Sign in to your account</p>
        </div>

        <form class="" @submit.prevent="handleLogin">
          <div class="mb-4">
            <label for="email" class="block text-sm font-semibold text-white/90 mb-2">Email Address</label>
            <input
              id="email"
              v-model="form.email"
              name="email"
              type="email"
              required
              class="glass-input"
              placeholder="Enter your email"
            />
          </div>

          <div class="mb-4">
            <label for="password" class="block text-sm font-semibold text-white/90 mb-2">Password</label>
            <input
              id="password"
              v-model="form.password"
              name="password"
              type="password"
              required
              class="glass-input"
              placeholder="Enter your password"
            />
          </div>

          <div v-if="error" class="mb-4 p-4 bg-red-500/20 border border-red-400/30 rounded-lg text-red-200 text-sm">
            {{ error }}
          </div>

          <button
            type="submit"
            :disabled="authStore.loading"
            class="w-full text-white py-3 px-4 mb-2 rounded-xl font-semibold focus:ring-2 focus:ring-white/20 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style="background: #2563eb;"
          >
            <span v-if="authStore.loading" class="flex items-center justify-center">
              <div class="loading mr-2"></div>
              Signing in...
            </span>
            <span v-else>Sign In</span>
          </button>
        
          <div class="text-center">
            <router-link to="/forgot-password" class="text-white/80 hover:text-white text-sm mb-4 block transition-colors">Forgot your password?</router-link>
            <div class="border-t border-white/20 mb-4"></div>
            <router-link to="/register" class="inline-block w-full bg-white/10 text-white/80 hover:text-white hover:bg-white/15 py-2 px-4 rounded-lg font-medium transition-all border border-white/20">Create Account</router-link>
          </div>
        </form>
      </div>

      <div class="mt-6 text-center">
        <p class="text-sm text-white/60">© 2026 Retailer Management System</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { LoginCredentials } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()

const form = ref<LoginCredentials>({
  email: '',
  password: ''
})

const error = ref('')

async function handleLogin() {
  error.value = ''
  try {
    await authStore.login(form.value)

    if (authStore.isSuperAdmin) await router.replace('/super-admin')
    else if (authStore.isAdminVendor) await router.replace('/dashboard')
    else if (authStore.isSalesVendor) await router.replace('/pos')
    else if (authStore.isCustomer) await router.replace('/catalog')
  } catch (err: unknown) {
    const apiError = (err as { error?: unknown })?.error
    const apiCode = (err as { code?: unknown })?.code
    error.value =
      typeof apiError === 'string'
        ? apiError
        : apiCode === 'INVALID_CREDENTIALS'
          ? 'Login failed. Please check your credentials.'
        : 'Login failed. Please check your credentials.'
  }
}

onMounted(() => {
  const notice = authStore.consumeAuthNotice()
  if (notice) {
    error.value = notice
  }
})
</script>

<style scoped>
.auth-background {
  background: #1e3a5f url('/images/auth-bg.png') center / cover no-repeat fixed;
  position: relative;
  isolation: isolate;
}

.auth-background::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 0;
}

.auth-background > * {
  position: relative;
  z-index: 1;
}

.glassmorphic-card {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  padding: 2rem;
  transition: all 0.3s ease;
}

.glassmorphic-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}

.glass-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.glass-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.glass-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.2);
}

.loading {
  width: 16px;
  height: 16px;
  border: 2px solid white;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

button:hover {
  background: #1d4ed8 !important;
}
</style>
